/**
 * Social API Service
 * Handles follow, like, and feed operations
 */
import { apiClient } from './apiClient';
import { Follow, Like, FeedItem, FollowStats } from '../../types/social.types';

export const socialService = {
  // ============================================================================
  // FOLLOW OPERATIONS
  // ============================================================================

  /**
   * Follow a user
   */
  followUser: async (userId: string) => {
    const response = await apiClient.post<{ follow: Follow }>(
      `/social/follow/${userId}`
    );
    return response.data;
  },

  /**
   * Unfollow a user
   */
  unfollowUser: async (userId: string) => {
    const response = await apiClient.delete(`/social/follow/${userId}`);
    return response.data;
  },

  /**
   * Get a user's followers
   */
  getFollowers: async (userId: string, page = 1, limit = 50) => {
    const response = await apiClient.get<{
      followers: Follow[];
      total: number;
      page: number;
    }>(`/social/followers/${userId}`, { params: { page, limit } });
    return response.data;
  },

  /**
   * Get users that a user is following
   */
  getFollowing: async (userId: string, page = 1, limit = 50) => {
    const response = await apiClient.get<{
      following: Follow[];
      total: number;
      page: number;
    }>(`/social/following/${userId}`, { params: { page, limit } });
    return response.data;
  },

  /**
   * Get follow statistics for a user
   */
  getFollowStats: async (userId: string) => {
    const response = await apiClient.get<FollowStats>(
      `/social/follow-stats/${userId}`
    );
    return response.data;
  },

  /**
   * Check if current user follows a specific user
   */
  checkFollowStatus: async (userId: string) => {
    const response = await apiClient.get<{ isFollowing: boolean }>(
      `/social/follow/status/${userId}`
    );
    return response.data;
  },

  // ============================================================================
  // LIKE OPERATIONS
  // ============================================================================

  /**
   * Like an entity (trip, profile, itinerary)
   */
  likeEntity: async (
    entityType: 'TRIP' | 'PROFILE' | 'ITINERARY',
    entityId: string
  ) => {
    const response = await apiClient.post<{ like: Like }>(
      `/social/like/${entityType}/${entityId}`
    );
    return response.data;
  },

  /**
   * Unlike an entity
   */
  unlikeEntity: async (
    entityType: 'TRIP' | 'PROFILE' | 'ITINERARY',
    entityId: string
  ) => {
    const response = await apiClient.delete(
      `/social/like/${entityType}/${entityId}`
    );
    return response.data;
  },

  /**
   * Get likes for an entity
   */
  getEntityLikes: async (
    entityType: string,
    entityId: string,
    page = 1,
    limit = 50
  ) => {
    const response = await apiClient.get<{ likes: Like[]; total: number }>(
      `/social/likes/${entityType}/${entityId}`,
      { params: { page, limit } }
    );
    return response.data;
  },

  /**
   * Check if current user likes an entity
   */
  checkLikeStatus: async (entityType: string, entityId: string) => {
    const response = await apiClient.get<{
      isLiked: boolean;
      likeCount: number;
    }>(`/social/like/status/${entityType}/${entityId}`);
    return response.data;
  },

  // ============================================================================
  // FEED OPERATIONS
  // ============================================================================

  /**
   * Get the current user's social feed
   */
  getUserFeed: async (params: { page: number; limit: number }) => {
    const response = await apiClient.get<{
      activities: FeedItem[];
      total: number;
      hasMore: boolean;
    }>('/social/feed', { params });
    return response.data;
  },

  /**
   * Get a specific user's activity (public profile feed)
   */
  getUserActivity: async (
    userId: string,
    params: { page: number; limit: number }
  ) => {
    const response = await apiClient.get<{
      activities: FeedItem[];
      total: number;
    }>(`/social/activity/${userId}`, { params });
    return response.data;
  },

  // ============================================================================
  // MUTUAL CONNECTIONS
  // ============================================================================

  /**
   * Get mutual followers between current user and another user
   */
  getMutualFollowers: async (userId: string) => {
    return apiClient.get<{ mutualFollowers: Follow[]; count: number }>(
      `/social/mutual-followers/${userId}`
    );
  },

  /**
   * Get suggested users to follow
   */
  getSuggestedUsers: async (limit = 10) => {
    return apiClient.get<{ suggestions: any[] }>('/social/suggestions', {
      params: { limit }
    });
  }
};
